/**
 * Client-side error reporting: sends browser errors (render crashes, uncaught
 * exceptions, unhandled rejections) to the server so they land in the same
 * error log that the `get_recent_errors` MCP tool reads.
 */

type ReportPayload = {
	message: string
	stack: string | null
	href: string
	userAgent: string
	kind: 'window.onerror' | 'unhandledrejection' | 'react-boundary'
	errorType?: string
}

/**
 * Mirrors the server schema caps in app/routes/resources+/client-errors.ts.
 * Truncate client-side just under the limits (the server 400s over-long
 * payloads, which would drop the whole report).
 */
const LIMITS = {
	message: 2000,
	stack: 10_000,
	href: 2000,
	userAgent: 500,
	errorType: 200,
} as const

const EMPTY_MESSAGE_FALLBACK = 'Unknown client error'
const TRUNCATED_SUFFIX = ' …[truncated]'

function truncateToLimit(value: string, max: number): string {
	if (value.length <= max) return value
	return (
		value.slice(0, Math.max(0, max - TRUNCATED_SUFFIX.length)) +
		TRUNCATED_SUFFIX
	)
}

function report(payload: ReportPayload) {
	try {
		// Normalize just under the server caps so one oversized field can never
		// get the entire report rejected with a 400. Empty messages are
		// coerced here too (window.onerror can legitimately produce them,
		// but the server schema requires min(1)).
		const normalized: ReportPayload = {
			...payload,
			message:
				truncateToLimit(payload.message, LIMITS.message) ||
				EMPTY_MESSAGE_FALLBACK,
			stack:
				payload.stack === null
					? null
					: truncateToLimit(payload.stack, LIMITS.stack),
			href: truncateToLimit(payload.href, LIMITS.href),
			userAgent: truncateToLimit(payload.userAgent, LIMITS.userAgent),
			errorType:
				payload.errorType === undefined
					? undefined
					: truncateToLimit(payload.errorType, LIMITS.errorType),
		}
		void fetch('/resources/client-errors', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(normalized),
			// keepAlive so in-flight reports survive page unload/crashes
			keepalive: true,
		}).catch(() => {})
	} catch {
		// never let reporting crash the app
	}
}

export function initClientErrorLogging() {
	if (typeof window === 'undefined') return

	window.addEventListener('error', (event) => {
		report({
			message: event.message ?? String(event.error ?? 'Unknown error'),
			stack: event.error instanceof Error ? (event.error.stack ?? null) : null,
			href: window.location.href,
			userAgent: navigator.userAgent,
			kind: 'window.onerror',
		})
	})

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason
		report({
			message:
				reason instanceof Error
					? reason.message
					: String(reason ?? 'Unhandled rejection'),
			stack: reason instanceof Error ? (reason.stack ?? null) : null,
			href: window.location.href,
			userAgent: navigator.userAgent,
			kind: 'unhandledrejection',
		})
	})
}

export function reportErrorToServer(
	error: unknown,
	options: { componentStack?: string | null } = {},
) {
	report({
		message: serializeErrorMessage(error),
		stack: serializeErrorStack(error, options.componentStack),
		href: window.location.href,
		userAgent: navigator.userAgent,
		kind: 'react-boundary',
		errorType: serializeErrorType(error),
	})
}

function serializeErrorType(error: unknown): string {
	if (error instanceof Error) return error.name
	if (typeof error === 'string') return 'string'
	if (error && typeof error === 'object') {
		const shape = ['status', 'statusText', 'data', 'message', 'stack'].filter(
			(key) => key in error,
		)
		if (shape.length) return `object{${shape.join(',')}}`
	}
	return error == null ? String(error) : typeof error
}

function serializeErrorMessage(error: unknown): string {
	if (error instanceof Error) return error.message
	if (typeof error === 'string') return error
	// React Router often hands the boundary a plain object, e.g.
	// {"status":405,"statusText":...,"data":"..."} thrown from an action.
	// Serialize it properly instead of losing all of it behind a placeholder.
	if (
		error &&
		typeof error === 'object' &&
		typeof (error as { message?: unknown }).message === 'string' &&
		(error as { message?: unknown }).message
	) {
		return (error as { message: string }).message
	}
	try {
		const json = JSON.stringify(error)
		if (json) return `Non-Error thrown: ${json}`
	} catch {
		// fall through (circular or non-serializable)
	}
	const described = error == null ? String(error) : typeof error
	return `Unknown client error (${described})`
}

function combineStacks(stack: string | null, componentStack?: string | null) {
	return componentStack
		? `${stack ? `${stack}\n` : ''}React component stack:${componentStack}`
		: stack
}

function serializeErrorStack(
	error: unknown,
	componentStack?: string | null,
): string | null {
	if (error instanceof Error)
		return combineStacks(error.stack ?? null, componentStack)
	if (componentStack) return combineStacks(null, componentStack)
	if (
		error &&
		typeof error === 'object' &&
		typeof (error as { stack?: unknown }).stack === 'string' &&
		(error as { stack?: unknown }).stack
	) {
		return (error as { stack: string }).stack
	}
	try {
		const json = JSON.stringify(error)
		if (json) return `JSON serialized value: ${json}`
	} catch {
		// fall through
	}
	return null
}
